import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;
const isPayPalConfigured = PAYPAL_CLIENT_ID && PAYPAL_CLIENT_ID !== 'your_paypal_client_id_here';

export default function CartPage() {
  const { cart, updateQuantity, removeFromCart, loading, fetchCart } = useCart();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const paypalRef = useRef(null);
  const [paypalReady, setPaypalReady] = useState(false);
  const [paypalError, setPaypalError] = useState(null);
  const [paypalLoading, setPaypalLoading] = useState(false);
  const isAuthenticated = Boolean(user && token);
  const hasUnavailableItems = cart.items.some((item) => {
    const stock = item.product?.stock ?? 0;
    return stock <= 0 || item.quantity > stock;
  });

  const loadPayPalScript = useCallback(() => {
    if (!isAuthenticated || !isPayPalConfigured || cart.items.length === 0 || hasUnavailableItems) return;

    const existingScript = document.getElementById('paypal-sdk');
    if (existingScript) {
      setPaypalReady(true);
      return;
    }

    setPaypalError(null);
    setPaypalLoading(true);
    const script = document.createElement('script');
    script.id = 'paypal-sdk';
    script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&currency=USD&intent=capture&enable-funding=venmo,card&disable-funding=credit,paylater&commit=true&vault=false`;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.onload = () => {
      if (!window.paypal) {
        setPaypalError('PayPal failed to load. Please disable blockers and refresh.');
        setPaypalLoading(false);
        return;
      }
      setPaypalReady(true);
      setPaypalLoading(false);
    };
    script.onerror = () => {
      setPaypalError('Failed to load PayPal. Please try again.');
      setPaypalLoading(false);
    };
    document.body.appendChild(script);
  }, [cart.items.length, hasUnavailableItems, isAuthenticated]);

  useEffect(() => {
    loadPayPalScript();
  }, [loadPayPalScript]);

  useEffect(() => {
    if (!isAuthenticated || !paypalReady || !window.paypal || !paypalRef.current || cart.items.length === 0 || hasUnavailableItems) return;

    paypalRef.current.innerHTML = '';
    try {
      window.paypal.Buttons({
        style: { 
          layout: 'vertical', 
          shape: 'rect', 
          label: 'paypal', 
          tagline: false, 
          height: 45 
        },
        createOrder: async () => {
          try {
            if (!isAuthenticated) {
              navigate('/login?redirect=/cart');
              return null;
            }
            // Convert JMD to USD for PayPal using product currency field
            const jmdToUsd = (jmd) => Math.round((jmd / 150) * 100) / 100;
            const items = cart.items.map((item) => {
              let usdPrice = item.product?.price;
              if (item.product?.currency === 'JMD') {
                usdPrice = jmdToUsd(usdPrice);
              }
              // If currency is USD, use as is
              return {
                product_id: item.product_id,
                quantity: item.quantity,
                price: usdPrice
              };
            });
            // Calculate total USD amount
            const totalUsd = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
            const response = await axios.post(
              `${API}/paypal/create-order`,
              { amount: totalUsd, currency: 'USD' },
              token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
            );
            if (!response.data.order_id) {
              throw new Error('Failed to create order');
            }
            return response.data.order_id;
          } catch (error) {
            console.error('Create order error:', error);
            toast.error('Failed to create order. Please try again.');
            throw error;
          }
        },
        onApprove: async (data, actions) => {
          try {
            // Verify the order before attempting capture
            if (!data.orderID) {
              throw new Error('Invalid order ID');
            }

            const response = await axios.post(
              `${API}/paypal/capture-order`, 
              { order_id: data.orderID },
              token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
            );
            
            // Only proceed if payment was actually completed
            if (response.data.status === 'COMPLETED') {
              toast.success('Payment successful!');
              localStorage.setItem('cart', JSON.stringify({ items: [], total: 0 }));
              await fetchCart();
              navigate(`/checkout/success?paypal_order_id=${data.orderID}`);
            } else {
              throw new Error(`Payment not completed. Status: ${response.data.status}`);
            }
          } catch (error) {
            console.error('PayPal capture error:', error);
            const errorMsg = error.response?.data?.detail || error.message || 'Payment failed';
            
            // Handle specific error cases
            if (error.response?.status === 402) {
              toast.error(errorMsg); // Insufficient funds or declined
            } else if (error.response?.status === 400) {
              toast.error(errorMsg); // Invalid payment method
            } else {
              toast.error('Payment failed. Please try again or use a different payment method.');
            }
            
            // Don't navigate away on error - let user try again
            return actions.restart();
          }
        },
        onCancel: (data) => {
          console.log('PayPal payment cancelled:', data);
          toast.info('Payment cancelled. Your cart is still available.');
        },
        onError: (err) => {
          console.error('PayPal SDK error:', err);
          toast.error('Payment system error. Please try again.');
          setPaypalError('Payment system encountered an error. Please refresh and try again.');
        }
      }).render(paypalRef.current);
    } catch (err) {
      console.error('PayPal render error:', err);
      setPaypalError('PayPal failed to initialize. Please refresh.');
    }
  }, [isAuthenticated, paypalReady, cart.items, fetchCart, navigate, hasUnavailableItems, token]);

  return (
    <div className="min-h-screen py-12 bg-cream-50" data-testid="cart-page">
      <div className="px-6 md:px-12 lg:px-24">
        <h1 className="font-heading text-3xl md:text-4xl text-leather-900 mb-2">Shopping Cart</h1>
        <p className="text-leather-600 mb-8">{cart.items.length} item{cart.items.length !== 1 ? 's' : ''} in cart</p>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="spinner"></div>
          </div>
        ) : cart.items.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-cart">
            <p className="text-leather-400 text-lg mb-6">Your cart is empty</p>
            <button
              onClick={() => navigate('/shop/belts')}
              className="btn-primary"
            >
              Start Shopping
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
              {cart.items.map((item) => (
                <div
                  key={item.product_id}
                  className="bg-white rounded-lg p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow flex flex-col sm:flex-row gap-4 sm:gap-6"
                  data-testid={`cart-item-${item.product_id}`}
                >
                  <div className="w-full h-48 sm:w-24 sm:h-24 md:w-28 md:h-28 bg-cream-100 flex-shrink-0 rounded-lg overflow-hidden">
                    <img
                      src={item.product?.images?.[0] || '/placeholder.jpg'}
                      alt={item.product?.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <h3 className="font-heading text-lg text-leather-900 mb-1">
                        {item.product?.name}
                      </h3>
                      <p className="text-leather-600 text-sm mb-2">{item.product?.category}</p>
                      <p className="font-medium text-gold-600">${item.product?.price?.toFixed(2)}</p>
                    </div>
                    
                    <div className="flex items-center justify-between pt-4">
                      <div className="flex items-center border border-cream-200 rounded-lg">
                        <button
                          onClick={() => updateQuantity(item.product_id, Math.max(0, item.quantity - 1))}
                          className="w-10 h-10 flex items-center justify-center hover:bg-cream-100 transition-colors"
                          title="Decrease quantity"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-10 text-center font-medium">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                          className="w-10 h-10 flex items-center justify-center hover:bg-cream-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Increase quantity"
                          disabled={item.product?.stock ? item.quantity >= item.product.stock : false}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      <button
                        onClick={() => {
                          removeFromCart(item.product_id);
                          toast.success('Item removed from cart');
                        }}
                        className="text-leather-400 hover:text-red-500 transition-colors p-2"
                        title="Remove from cart"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  <div className="text-left sm:text-right flex flex-col justify-between">
                    <p className="font-semibold text-leather-900">
                      ${(item.product?.price * item.quantity).toFixed(2)}
                    </p>
                    <p className={`text-xs mt-2 ${item.product?.stock > 0 && item.quantity <= item.product.stock ? 'text-green-600' : 'text-red-500'}`}>
                      {item.product?.stock > 0
                        ? item.quantity > item.product.stock
                          ? `Only ${item.product.stock} left`
                          : 'In Stock'
                        : 'Out of Stock'}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Order Summary Sidebar */}
            <div className="lg:col-span-1" data-testid="order-summary">
              <div className="bg-white rounded-lg p-8 shadow-sm sticky top-24">
                <h2 className="font-heading text-2xl text-leather-900 mb-6">Order Summary</h2>
                
                <div className="space-y-4 mb-6 pb-6 border-b border-cream-200">
                  <div className="flex justify-between text-sm">
                    <span className="text-leather-600">Subtotal</span>
                    <span className="text-leather-900 font-medium">${cart.total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-leather-600">Shipping</span>
                    <span className="text-leather-600">Calculated at checkout</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-leather-600">Tax</span>
                    <span className="text-leather-600">Calculated at checkout</span>
                  </div>
                </div>

                <div className="mb-8">
                  <div className="flex justify-between items-center">
                    <span className="font-heading text-leather-900 text-lg">Total</span>
                    <span className="font-heading text-gold-600 text-2xl">${cart.total.toFixed(2)}</span>
                  </div>
                </div>

                <div className="space-y-4">
                  {!isAuthenticated && (
                    <div className="rounded-lg border border-gold-200 bg-cream-100 p-4 text-sm text-leather-700">
                      <p className="mb-3">Please sign in to complete checkout.</p>
                      <button
                        onClick={() => navigate('/login?redirect=/cart')}
                        className="btn-secondary w-full"
                      >
                        Sign In to Checkout
                      </button>
                    </div>
                  )}
                  <p className="text-center text-sm text-leather-600">
                    Pay with debit/credit card — PayPal account not required.
                  </p>
                  {hasUnavailableItems && (
                    <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 rounded-lg text-center">
                      Some items are unavailable. Please adjust quantities to continue checkout.
                    </div>
                  )}
                  {isPayPalConfigured ? (
                    <div className="space-y-3">
                      {paypalError && (
                        <p className="text-sm text-red-500 text-center">{paypalError}</p>
                      )}
                      {!paypalReady && !paypalLoading && !hasUnavailableItems && (
                        <p className="text-sm text-leather-600 text-center">Loading PayPal…</p>
                      )}
                      {paypalLoading && !hasUnavailableItems && (
                        <div className="flex items-center justify-center py-4">
                          <div className="w-5 h-5 border-2 border-leather-900 border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      )}
                      <p className="text-xs text-leather-500 text-center">
                        Pay securely with PayPal or debit/credit card
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-700 text-center font-medium">
                        Payment processing is currently unavailable
                      </p>
                      <p className="text-xs text-red-600 text-center">
                        Please contact support@beltandtingz.com to complete your order
                      </p>
                    </div>
                  )}
                  {/* The PayPal button container is always present in the DOM, never hidden or removed */}
                  <div ref={paypalRef} />

                  <button
                    onClick={() => navigate('/shop/belts')}
                    className="w-full px-6 py-3 border border-leather-900 text-leather-900 rounded-lg hover:bg-cream-50 transition-colors"
                  >
                    Continue Shopping
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
